'use client'
import { logger } from '@/lib/logger'
import { warnIfMapsKeyMissing } from '@/src/lib/maps-config-guard'
import { useEffect, useRef, useState } from 'react'
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps'
import { MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useRuntimeConfigValue } from '@/providers/RuntimeConfigProvider'

interface AddressAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onPlaceSelect?: (place: google.maps.places.PlaceResult) => void
  placeholder?: string
  className?: string
  id?: string
  showIcon?: boolean
  disabled?: boolean
}

const defaultInputStyles = cn(
  'w-full bg-background text-foreground placeholder:text-muted-foreground/60',
  'px-2.5 py-2 sm:py-1.5 text-sm sm:text-xs',
  'border border-input rounded-md',
  'focus:outline-none focus:ring-1 focus:ring-primary',
  'transition-shadow duration-150 touch-manipulation',
  'disabled:opacity-50 disabled:cursor-not-allowed'
)

/** Fallback input when Google Maps is unavailable */
function FallbackInput({
  value,
  onChange,
  placeholder,
  className,
  id,
  showIcon,
  disabled,
}: Omit<AddressAutocompleteProps, 'onPlaceSelect'>) {
  const input = (
    <input
      type="text"
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(defaultInputStyles, showIcon && 'pl-8', className)}
      placeholder={placeholder}
      disabled={disabled}
    />
  )

  if (showIcon) {
    return (
      <div className="relative">
        <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        {input}
      </div>
    )
  }

  return input
}

/** Internal component with Google Autocomplete */
function AddressAutocompleteInput({
  value,
  onChange,
  onPlaceSelect,
  placeholder = 'Search for address...',
  className,
  id,
  showIcon = true,
  disabled = false,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const places = useMapsLibrary('places')
  const [localValue, setLocalValue] = useState(value)

  const callbacksRef = useRef({ onChange, onPlaceSelect })

  useEffect(() => {
    callbacksRef.current = { onChange, onPlaceSelect }
  }, [onChange, onPlaceSelect])

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  useEffect(() => {
    if (!places || !inputRef.current) return

    const autocomplete = new places.Autocomplete(inputRef.current, {
      fields: [
        'formatted_address',
        'geometry',
        'name',
        'place_id',
        'address_components',
      ],
    })

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()
      const address = place.formatted_address || place.name || ''

      if (address) {
        setLocalValue(address)
        callbacksRef.current.onChange(address)
        callbacksRef.current.onPlaceSelect?.(place)
      }
    })

    return () => {
      google.maps.event.removeListener(listener)
      google.maps.event.clearInstanceListeners(autocomplete)
    }
  }, [places])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setLocalValue(newValue)
    callbacksRef.current.onChange(newValue)
  }

  const input = (
    <input
      ref={inputRef}
      type="text"
      id={id}
      value={localValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      className={cn(defaultInputStyles, showIcon && 'pl-8', className)}
    />
  )

  if (showIcon) {
    return (
      <div className="relative">
        <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        {input}
      </div>
    )
  }

  return input
}

/** Address input with Google Places Autocomplete */
export default function AddressAutocomplete({
  showIcon = true,
  disabled = false,
  placeholder = 'Search for address...',
  ...props
}: AddressAutocompleteProps) {
  const apiKey = useRuntimeConfigValue('NEXT_PUBLIC_GOOGLE_MAPS_API_KEY')
  const [hasError, setHasError] = useState(false)

  if (!apiKey || hasError) {
    if (!apiKey) {
      warnIfMapsKeyMissing(apiKey, 'AddressAutocomplete')
    }
    if (hasError) {
      logger.error('Google Maps API failed to load, using fallback input')
    }
    return (
      <FallbackInput
        {...props}
        showIcon={showIcon}
        disabled={disabled}
        placeholder={placeholder}
      />
    )
  }

  return (
    <APIProvider
      apiKey={apiKey}
      onError={(error) => {
        logger.error('Google Maps API error:', error)
        setHasError(true)
      }}
    >
      <AddressAutocompleteInput
        {...props}
        showIcon={showIcon}
        disabled={disabled}
        placeholder={placeholder}
      />
    </APIProvider>
  )
}
